import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage, authApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { HyreDocsIcon } from '../components/HyreDocsLogo';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState('bullhorn'); // 'bullhorn' or 'password'
  const { setUser, setTenant } = useAuth();
  const navigate = useNavigate();

  // Reset form when switching modes
  const handleModeSwitch = (mode) => {
    setLoginMode(mode);
    setEmail('');
    setPassword('');
    setShowPassword(false);
  };

  // Handle Bullhorn direct login (username + password)
  const handleBullhornLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter your Bullhorn username and password');
      return;
    }

    setLoading(true);
    try {
      const response = await authApi.bullhornDirectLogin(email, password);
      const { user, tenant } = response.data;

      // Cookie is set automatically by the backend response
      setUser(user);
      setTenant(tenant);
      localStorage.setItem('tenantSlug', tenant.slug);

      toast.success(`Welcome, ${user.name}!`);
      navigate(`/${tenant.slug}/dashboard`);
    } catch (error) {
      const message = getErrorMessage(error, 'Invalid Bullhorn credentials');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Handle HyreDocs password login (for master admin, etc.)
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, { 
        email, 
        password 
      });
      const { user, tenant } = response.data;

      // Cookie is set automatically by the backend response
      setUser(user);
      
      if (user.is_master_admin) {
        navigate('/master');
      } else {
        setTenant(tenant);
        localStorage.setItem('tenantSlug', tenant.slug);
        navigate(`/${tenant.slug}/dashboard`);
      }
      
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Invalid credentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F9F8F6]">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#1e293b] via-[#334155] to-[#1e293b] p-12 flex-col justify-between relative overflow-hidden">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#f97316] to-[#f59e0b] flex items-center justify-center shadow-lg shadow-orange-500/30">
              <HyreDocsIcon className="w-6 h-6" />
            </div>
            <span className="font-heading font-bold text-2xl text-white">HyreDocs</span>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-[#f97316]/20 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-40 left-10 w-48 h-48 bg-gradient-to-br from-[#f59e0b]/15 to-transparent rounded-full blur-3xl" />
        
        <div className="space-y-6 relative z-10">
          <h2 className="font-heading text-4xl font-bold text-white leading-tight">
            Onboarding docs and<br />client contracts,<br />synced to Bullhorn.
          </h2>
          
          <ul className="space-y-3 text-slate-300 font-body text-sm max-w-md">
            <li className="flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-[#f97316]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#f97316]"></span>
              </span>
              <span><strong className="text-white">Sync-back that works.</strong> Every field and file lands on the right Bullhorn record automatically.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-[#f97316]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#f97316]"></span>
              </span>
              <span><strong className="text-white">One tool, two audiences.</strong> Candidate onboarding packs and client contracts from the same dashboard.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-[#f97316]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#f97316]"></span>
              </span>
              <span><strong className="text-white">Audit-ready.</strong> Every signed document includes a tamper-evident certificate (ESIGN, UETA, eIDAS).</span>
            </li>
          </ul>
        </div>

        <div className="relative z-10 text-xs text-slate-400 font-body">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
            <span>Operational  ·  Native Bullhorn integration</span>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#f97316] to-[#f59e0b] flex items-center justify-center shadow-md">
              <HyreDocsIcon className="w-5 h-5" />
            </div>
            <span className="font-heading font-bold text-xl">HyreDocs</span>
          </div>

          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">Sign in</h1>
            <p className="text-muted-foreground font-body mt-2">
              {loginMode === 'bullhorn'
                ? 'Use your Bullhorn credentials to continue'
                : 'Sign in with your HyreDocs email and password'}
            </p>
          </div>

          {/* Login mode tabs */}
          <div
            className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-slate-100 border border-slate-200"
            data-testid="login-mode-tabs"
            role="tablist"
          >
            <button
              type="button"
              role="tab"
              aria-selected={loginMode === 'bullhorn'}
              onClick={() => handleModeSwitch('bullhorn')}
              className={`py-2 px-3 text-sm font-medium rounded-md transition-all ${
                loginMode === 'bullhorn'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="login-tab-bullhorn"
            >
              Bullhorn Login
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={loginMode === 'password'}
              onClick={() => handleModeSwitch('password')}
              className={`py-2 px-3 text-sm font-medium rounded-md transition-all ${
                loginMode === 'password'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="login-tab-password"
            >
              HyreDocs Login
            </button>
          </div>

          {loginMode === 'bullhorn' ? (
            /* Bullhorn Login Form */
            <form onSubmit={handleBullhornLogin} className="space-y-6" data-testid="bullhorn-login-form">
              <div className="space-y-2">
                <Label htmlFor="bullhorn-username" className="font-body">Bullhorn Username</Label>
                <Input
                  id="bullhorn-username"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.username"
                  required
                  className="input-focus"
                  data-testid="bullhorn-username-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="bullhorn-password" className="font-body">Bullhorn Password</Label>
                </div>
                <div className="relative">
                  <Input
                    id="bullhorn-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your Bullhorn password"
                    required
                    className="input-focus pr-10"
                    data-testid="bullhorn-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 gap-2 bg-[#0072CE] hover:bg-[#005ba1] text-white"
                disabled={loading}
                data-testid="bullhorn-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signing in to Bullhorn...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                    </svg>
                    Sign in with Bullhorn
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                Same credentials as your Bullhorn account. We validate them directly against Bullhorn — HyreDocs never stores them.
              </p>
            </form>
          ) : (
            /* HyreDocs password login form */
            <form onSubmit={handlePasswordLogin} className="space-y-6" data-testid="password-login-form">
              <div className="space-y-2">
                <Label htmlFor="hyredocs-email" className="font-body">Email</Label>
                <Input
                  id="hyredocs-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  className="input-focus"
                  data-testid="hyredocs-email-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="hyredocs-password" className="font-body">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-[#f97316] hover:text-[#ea580c] font-medium"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="hyredocs-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your HyreDocs password"
                    required
                    className="input-focus pr-10"
                    data-testid="hyredocs-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 gap-2 bg-[#f97316] hover:bg-[#ea580c] text-white"
                disabled={loading}
                data-testid="hyredocs-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign in to HyreDocs'
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                For admins and team members with a HyreDocs account. Use "Bullhorn Login" above if you sign in with your Bullhorn credentials.
              </p>
            </form>
          )}

            {/* Help + signup guidance */}
            <div className="pt-6 mt-6 border-t border-slate-200 space-y-2 text-center">
              <p className="text-xs text-muted-foreground">
                Not a HyreDocs customer yet? HyreDocs is provisioned per agency.{' '}
                <a 
                  href="mailto:hello@hyredocs.com?subject=HyreDocs%20Demo%20Request" 
                  className="text-[#f97316] hover:text-[#ea580c] font-medium"
                  data-testid="book-demo-link"
                >
                  Book a demo →
                </a>
              </p>
              <p className="text-xs text-muted-foreground">
                Need help signing in?{' '}
                <a 
                  href="mailto:support@hyredocs.com" 
                  className="text-[#f97316] hover:text-[#ea580c] font-medium"
                  data-testid="support-link"
                >
                  Contact support
                </a>
              </p>
            </div>
        </div>
      </div>
    </div>
  );
}
